/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, memo, useCallback } from 'react';

//external packages
import { withRouter } from 'react-router-dom';
import styled, { css } from 'styled-components';

//internal UI packages
import { Timer } from '@cuemath/intel';
import { Text, Separator } from '@cuemath/web-comps';
import { useRemotePeers, useLocalPeer, useAVActions } from '@cuemath/av';
import { COLORS, FlexView } from '@cuemath/comps';
//icons
import { Videocam } from 'styled-icons/material/Videocam';
import { VideocamOff } from 'styled-icons/material/VideocamOff';
import { Mic } from 'styled-icons/material/Mic';
import { MicOff } from 'styled-icons/material/MicOff';
// import { Timer as TimerIcon } from 'styled-icons/material/Timer';
import { ReactComponent as TimerSVG } from 'img/clock.svg';

//components
import Video from './video';

import { resetAVSessionToken } from 'actions/av/av-session-token';

//constants & utilities
import { getCurrentTimeStr } from '../utils';
import useAVHelper from '../../../hooks/useAVHelper';
import { useDispatch } from 'react-redux';
import { AVRemoteVideo } from '@cuemath/av';

const ACADEMIC_COUNSELOR = 'Academic Counselor';

const Wrapper = styled.div`
  width: 100%;
  height: 100vh;
  overflow: hidden;
  position: relative;
  background: ${COLORS.GRAY_XXXLIGHT};
`;
const MediaWrapper = styled(FlexView)`
  width: 100%;
  ${({ isScreenShared }) =>
    isScreenShared &&
    css`
      position: absolute;
      top: 0;
      left: 0;
      bottom: 76px;
    `}
`;
const TimerWrapper = styled(FlexView)`
  width: max-content;
  margin: auto;
  gap: 8px;
`;
const TimerCenter = styled(FlexView)`
  background: ${COLORS.SUN_XXLIGHT};
  padding: 4px 20px 4px 12px;
  gap: 8px;
  border-radius: 32px;
`;
const TimerIcon = styled(TimerSVG)`
  path {
    stroke: ${COLORS.GRAY};
  }
`;
const VideosWrapper = styled(FlexView)`
  background: ${COLORS.WHITE};
  border-radius: 12px;
  width: max-content;
  padding: 40px;
  margin: auto;
  ${({ isScreenShared }) =>
    isScreenShared &&
    css`
      max-width: 262px;
      background: ${COLORS.GRAY_XXXLIGHT};
      padding: 24px;
      margin: 0;
    `}
`;
const VideosInnerWrapper = styled.div`
  flex-direction: row;
  display: flex;
  height: ${({ participants }) => (participants === 1 ? 100 : 100 - participants * 10)}%;
  flex: 1;
  & > div {
    flex: 1;
  }
  ${({ isScreenShared }) =>
    isScreenShared &&
    css`
      flex-direction: column;
      height: 100%;
    `}
`;
const Footer = styled.div`
  display: flex;
  width: 100%;
  height: 10%;
  justify-content: space-between;
  padding: 1rem;
  align-items: center;
  background: ${COLORS.BLACK};
  position: absolute;
  bottom: 0;
  right: 0;
  left: 0;
`;
const FooterLeft = styled.div`
  color: #fff;
  flex: 1;
`;
const FooterCenter = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
`;
const FooterRight = styled.div`
  color: #fff;
  flex: 1;
`;
const IconWrapper = styled.div`
  border: 2px solid ${({ isActive }) => (isActive ? '#3c4043' : 'rgb(0, 200, 255)')};
  background: ${({ isActive }) => (isActive ? '#3c4043' : 'rgb(0, 200, 255)')};
  border-radius: 50%;
  padding: 0.5rem;
  margin-right: ${props => props.mr}rem;
  cursor: pointer;
`;
const ScreenWrapper = styled.div`
  height: 100%;
  & > div {
    height: 100%;
    > video {
      height: 100%;
      object-fit: contain;
    }
  }
`;

const CallScreen = props => {
  const { classStatus, onEndClass, teacherClassroomId, userId } = props;

  const [currentTime, setCurrentTime] = useState('');
  const [participants, setParticipants] = useState(1);

  const dispatch = useDispatch();
  const remotePeers = useRemotePeers();
  const peer = useLocalPeer();
  const { enableAudio, enableVideo } = useAVActions();

  const teacherPeer = remotePeers.find(peer => peer.userType === 'teacher');
  const studentPeer = remotePeers.find(peer => peer.userType === 'student');

  const { av, id: peerId } = peer || {};
  const { hasAudio, hasVideo } = av || {};

  const {
    scheduled_end_datetime: classEndTime,
    turnserver_credentials: turnServerCredentials,
    class_type: classType,
  } = classStatus;

  const onTimerComplete = () => {
    onEndClass();
  };

  useEffect(() => {
    setCurrentTime(getCurrentTimeStr());
    const intervalId = setInterval(
      () => setCurrentTime(() => getCurrentTimeStr()),
      60000,
    );
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  const toggleAudio = useCallback(() => {
    if (peerId) {
      enableAudio(peerId, !hasAudio);
    }
  }, [peerId, enableAudio, teacherClassroomId, hasAudio]);

  const toggleVideo = useCallback(() => {
    if (peerId) {
      enableVideo(peerId, !hasVideo);
    }
  }, [peerId, enableAudio, teacherClassroomId, hasVideo]);

  const { isLoaded, fetchTokens, join, leave } = useAVHelper({
    provider: 'tokbox',
    turnServerCredentials,
    name: 'Teacher',
    teacherClassroomId,
    userId,
    classType,
  });

  const joinCall = useCallback(() => {
    join({
      audioEnabled: true,
      videoEnabled: true,
      canPublish: true,
      virtualBackgroundEnabled: true,
    });
  }, [join]);

  useEffect(() => {
    fetchTokens();
    // Fetch token only on initial render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isLoaded) {
      joinCall();
    }
  }, [isLoaded, joinCall]);

  useEffect(() => {
    return () => {
      dispatch(resetAVSessionToken());
      leave();
    };
  }, [leave]);

  useEffect(() => {
    if (teacherPeer?.av?.stream && studentPeer?.av?.stream) {
      setParticipants(3);
    } else if (teacherPeer?.av?.stream || studentPeer?.av?.stream) {
      setParticipants(2);
    } else {
      setParticipants(1);
    }
  }, [teacherPeer, studentPeer]);

  const isTeacherScreenShared = teacherPeer?.screen?.hasScreen;

  return (
    <Wrapper>
      <Separator height={80} />
      <MediaWrapper
        flexDirection="row"
        alignItems="start"
        justifyContent="space-between"
        isScreenShared={isTeacherScreenShared}
      >
        <VideosWrapper isScreenShared={isTeacherScreenShared}>
          {classEndTime && (
            <TimerWrapper flexDirection="row" alignItems="center" justifyContent="center">
              <Text variant="body1Regular">Ongoing:</Text>
              <TimerCenter
                flexDirection="row"
                alignItems="center"
                justifyContent="center"
              >
                <TimerIcon />
                <Timer
                  color={COLORS.BLACK}
                  endTime={classEndTime}
                  hideHourUnit
                  onComplete={onTimerComplete}
                  size="normal"
                  display="inline"
                />
              </TimerCenter>
            </TimerWrapper>
          )}
          <Separator height={isTeacherScreenShared ? 8 : 26} />
          {!isTeacherScreenShared && (
            <>
              <Text variant="h4" align="center">
                Welcome to trial class!
              </Text>
              <Separator height={48} />
            </>
          )}
          <VideosInnerWrapper
            isScreenShared={isTeacherScreenShared}
            participants={participants}
          >
            {teacherPeer?.av?.stream && (
              <Video
                peer={teacherPeer}
                userDetails={{
                  name: 'Teacher',
                  fullName: 'Teacher',
                }}
                hasAudio={teacherPeer?.av?.hasAudio}
                hasVideo={teacherPeer?.av?.hasVideo}
                isLocal={false}
              />
            )}
            {studentPeer?.av?.stream && (
              <Video
                peer={studentPeer}
                userDetails={{
                  name: 'Student',
                  fullName: 'Student',
                }}
                hasAudio={studentPeer?.av?.hasAudio}
                hasVideo={studentPeer?.av?.hasVideo}
                isLocal={false}
              />
            )}
            {peer && (
              <Video
                peer={peer}
                userDetails={{
                  name: 'You',
                  fullName: ACADEMIC_COUNSELOR,
                }}
                hasAudio={hasAudio}
                hasVideo={hasVideo}
                isLocal={true}
              />
            )}
          </VideosInnerWrapper>
        </VideosWrapper>
        <ScreenWrapper>
          {isTeacherScreenShared && (
            <AVRemoteVideo peer={teacherPeer} videoType="screen" />
          )}
        </ScreenWrapper>
      </MediaWrapper>

      <Footer>
        <FooterLeft>{currentTime} | Student Demo</FooterLeft>
        <FooterCenter>
          <IconWrapper mr={1} onClick={toggleVideo} isActive={hasVideo}>
            {hasVideo ? (
              <Videocam size={24} color="#fff" />
            ) : (
              <VideocamOff size={24} color="#fff" />
            )}
          </IconWrapper>
          <IconWrapper mr={0} onClick={toggleAudio} isActive={hasAudio}>
            {hasAudio ? (
              <Mic size={24} color="#fff" />
            ) : (
              <MicOff size={24} color="#fff" />
            )}
          </IconWrapper>
        </FooterCenter>
        <FooterRight />
      </Footer>
    </Wrapper>
  );
};

export default withRouter(memo(CallScreen));
